import i18next from 'i18next';
import LngDetector from 'i18next-browser-languagedetector';
import countries from "i18n-iso-countries";
// @ts-ignore
import moment from 'moment-timezone';
import dayjs from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import weekday from 'dayjs/plugin/weekday';
import duration from 'dayjs/plugin/duration';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import isBetween from 'dayjs/plugin/isBetween';
dayjs.extend(localeData);
dayjs.extend(customParseFormat);
dayjs.extend(localizedFormat);
dayjs.extend(weekday);
dayjs.extend(duration);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(advancedFormat);
dayjs.extend(isBetween);
import 'dayjs/locale/sv';
export class Localization {
    static getLocales() {
        return {
            en: {
                base: require('../locales/en/base.json')
            },
            sv: {
                base: require('../locales/sv/base.json')
            },
            es: {
                base: require('../locales/es/base.json')
            }
        };
    }
    static mergeLocales(a, b) {
        const keys = Array.from(new Set([...Object.keys(a), ...Object.keys(b)]));
        return keys.reduce((acc, key) => {
            acc[key] = Object.assign({}, a[key], b[key]);
            return acc;
        }, {});
    }
    static init(locales) {
        const appLocales = Localization.mergeLocales(locales, Localization.getLocales());
        const defaultNS = Object.keys(locales).reduce((acc, locale) => {
            return acc.concat(Object.keys(locales[locale]));
        }, [])[0];
        return i18next
            .use(LngDetector)
            .init({
            fallbackLng: 'en',
            resources: appLocales,
            interpolation: {
                prefix: "__",
                suffix: "__"
            },
            defaultNS: defaultNS ? defaultNS : "base",
            fallbackNS: defaultNS ? "base" : false
        }).then(() => {
            moment.locale(i18next.language === 'sv' ? 'sv-se' : 'en-gb');
            dayjs.locale(i18next.language === 'sv' ? 'sv' : 'en');
            if (countries.getSupportedLanguages().includes(i18next.language)) {
                countries.registerLocale(require("i18n-iso-countries/langs/" + i18next.language + ".json"));
            }
            else {
                countries.registerLocale(require("i18n-iso-countries/langs/en.json"));
            }
        });
    }
    static getLanguage() {
        return i18next.language;
    }
    static getCountryNames() {
        const supportedLanguages = countries.getSupportedLanguages();
        const currentLanguage = supportedLanguages.includes(i18next.language) ? i18next.language : 'en';
        const countryNames = countries.getNames(currentLanguage) || {};
        // Handle empty data case
        if (Object.keys(countryNames).length === 0) {
            return {};
        }
        // Sort and return the country names
        const sortedEntries = Object.entries(countryNames).sort(([, nameA], [, nameB]) => {
            return nameA.localeCompare(nameB);
        });
        return Object.fromEntries(sortedEntries);
    }
    static getCountryName(code) {
        const names = this.getCountryNames();
        if (countries.isValid(code)) {
            return names[code];
        }
        else {
            return code;
        }
    }
    static guessUserCountry() {
        var _a, _b, _c;
        const normalizeIso2 = (code) => {
            if (!code)
                return undefined;
            const upper = code.toUpperCase();
            return countries.isValid(upper) ? upper : undefined;
        };
        // 1) Language with region (sv-SE → SE)
        try {
            const tryRegionFromLang = (lng) => {
                if (!lng)
                    return undefined;
                const parts = lng.trim().split('-');
                if (parts.length >= 2)
                    return normalizeIso2(parts[1]);
                return undefined;
            };
            const langWithRegion = tryRegionFromLang(i18next.language) ||
                (Array.isArray(i18next.languages)
                    ? i18next.languages.map((l) => tryRegionFromLang(l)).find(Boolean)
                    : undefined);
            if (langWithRegion)
                return langWithRegion;
        }
        catch { }
        // 2) Time zone → country
        try {
            const tzApi = moment.tz;
            const tzName = ((_c = (_a = Intl === null || Intl === void 0 ? void 0 : Intl.DateTimeFormat) === null || _a === void 0 ? void 0 : (_b = _a.call(Intl)).resolvedOptions) === null || _c === void 0 ? void 0 : _c.call(_b).timeZone) ||
                (typeof (tzApi === null || tzApi === void 0 ? void 0 : tzApi.guess) === 'function' ? tzApi.guess() : undefined);
            if (tzApi && typeof tzApi.countries === 'function' && typeof tzApi.zonesForCountry === 'function' && tzName) {
                const allCountries = tzApi.countries() || [];
                for (const c of allCountries) {
                    const zones = tzApi.zonesForCountry(c);
                    const names = Array.isArray(zones)
                        ? zones.map((z) => (typeof z === 'string' ? z : (z && z.name) || ''))
                        : [];
                    if (names.includes(tzName)) {
                        const iso2 = normalizeIso2(c);
                        if (iso2)
                            return iso2;
                    }
                }
            }
        }
        catch { }
        // 3) Language → default country map
        try {
            const lng = (i18next.language || 'en').slice(0, 2).toLowerCase();
            const langMap = {
                sv: 'SE', no: 'NO', nb: 'NO', nn: 'NO',
                da: 'DK', fi: 'FI',
                en: 'US', de: 'DE', fr: 'FR', es: 'ES',
                pt: 'PT', nl: 'NL', it: 'IT', pl: 'PL'
            };
            const mapped = normalizeIso2(langMap[lng]);
            if (mapped)
                return mapped;
        }
        catch { }
        return undefined;
    }
    static isValidCountryCode(code) {
        return countries.isValid(code);
    }
    static changeLanguage(lang) {
        return new Promise((resolve, reject) => {
            i18next.changeLanguage(lang || i18next.services.languageDetector.detect(), function (err) {
                if (err) {
                    return reject(err);
                }
                if (countries.getSupportedLanguages().includes(i18next.language)) {
                    countries.registerLocale(require("i18n-iso-countries/langs/" + i18next.language + ".json"));
                }
                else {
                    countries.registerLocale(require("i18n-iso-countries/langs/en.json"));
                }
                resolve();
            });
        });
    }
    ;
    static onLanguageChange(callback) {
        i18next.on('languageChanged', callback);
    }
    ;
    static getLocalizedString(strings) {
        return strings && (strings[i18next.language] || strings.default);
    }
    ;
}
export function _T(key, ...args) {
    const options = Object.assign({}, ...args, {
        interpolation: {
            escapeValue: false, // Ensures that interpolation values are not escaped.
        },
    });
    return i18next.t.call(i18next, key, options);
}
